// SUB-OFFICE 7 — COMPOSE. A real DOM textarea styled as the paper, floated
// over the dimmed hall while the camera performs its 900ms two-phase rise to
// true overhead. The FSM holds COMPOSE for the duration: WASD/E/Space are
// dead (input.ts honors both the state gate and DOM focus), the textarea owns
// the keyboard absolutely.
//
// Variants: standard (checklist + telemetry), attestation (exact typing,
// errors counted and restarted), patience (stillness past a generous 0.25
// deadzone; fidgeting blurs the ink and restarts the line), sorting (the pile
// is patient). Leaving mid-composition is permitted and filed:
// 'DRAFT ABANDONED. The Bureau read it anyway.'
// Offerings under 60 characters occasionally demand SUBMIT IN TRIPLICATE.

import type { PerspectiveCamera } from 'three';
import { Vector3 } from 'three';
import { liveChecklist } from '../../shared/constraints';
import { fnv1a } from '../../shared/rng';
import type { Constraint, RitualDef, Telemetry } from '../../shared/types';
import { CompositionMeter } from '../telemetry';
import { printLayer } from './chrome';
import type { EventBus } from '../entities/entities';
import type { SceneManager } from '../render/scene';

export interface ComposeSpec {
  formId: string; // 'FORM 7A-112'
  demand: string; // demand text as printed (constraints included)
  constraints: Constraint[];
  designation: string;
  mode: 'standard' | 'attestation' | 'patience' | 'sorting';
  ritual?: RitualDef;
  demandId: string; // seeds the triplicate roll
  draft?: string; // an aborted submit chain returns the form to hand
  overheadAt?: { x: number; z: number }; // desk/lectern world position
  submitLabel?: string;
  closable?: boolean; // false = intake forms that cannot be walked away from
}

export interface ComposeResult {
  abandoned: boolean;
  text: string;
  telemetry: Telemetry;
  completed?: boolean; // patience / sorting
  imeDowngraded: boolean;
}

const TRIPLICATE_MOD = 6; // <60-char offerings: 1-in-6 demand triplicate

export class ComposeOverlay {
  private open = false;

  constructor(private bus: EventBus, private sceneMgr: SceneManager) {}

  get isOpen(): boolean {
    return this.open;
  }

  compose(spec: ComposeSpec): Promise<ComposeResult> {
    this.open = true;
    return new Promise((resolve) => {
      const layer = printLayer();
      const dim = document.createElement('div');
      dim.className = 'compose-dim';
      layer.appendChild(dim);

      const sheet = document.createElement('div');
      sheet.className = 'compose-sheet';
      layer.appendChild(sheet);

      const camCleanup = spec.overheadAt ? this.overheadCue(spec.overheadAt) : null;

      const done = (r: ComposeResult): void => {
        this.open = false;
        dim.remove();
        sheet.remove();
        if (camCleanup) camCleanup();
        resolve(r);
      };

      if (spec.mode === 'patience') this.buildPatience(sheet, spec, done);
      else if (spec.mode === 'sorting') this.buildSorting(sheet, spec, done);
      else this.buildTyping(sheet, spec, done);
    });
  }

  // -------------------------------------------------------------------------
  // Camera: 900ms two-phase rise-then-pitch to true overhead (89 degrees).
  // Returns a cleanup that plays the 400ms descent and releases the cue.
  // -------------------------------------------------------------------------

  private overheadCue(at: { x: number; z: number }): () => void {
    const mgr = this.sceneMgr;
    let t = 0;
    let closing = false;
    let closeT = 0;
    const startPos = new Vector3();
    const endPos = new Vector3(at.x, 8.5, at.z + 0.9);
    const lookNear = new Vector3(at.x, 0.9, at.z);
    const lookDown = new Vector3(at.x, 0, at.z);
    const tmp = new Vector3();
    let captured = false;
    const smooth = (k: number): number => k * k * (3 - 2 * k);

    mgr.pushCue((dt: number, camera: PerspectiveCamera): boolean => {
      if (!captured) {
        captured = true;
        startPos.copy(camera.position);
      }
      if (!closing) {
        t = Math.min(0.9, t + dt);
        const k = smooth(t / 0.9);
        camera.position.lerpVectors(startPos, endPos, k);
        // phase 1 keeps the subject; phase 2 pitches to the paper
        const pitchK = smooth(Math.max(0, (t - 0.45) / 0.45));
        tmp.lerpVectors(lookNear, lookDown, pitchK);
        camera.lookAt(tmp);
        return true;
      }
      closeT = Math.min(0.4, closeT + dt);
      const k = 1 - smooth(closeT / 0.4);
      camera.position.lerpVectors(startPos, endPos, k);
      tmp.lerpVectors(lookNear, lookDown, k);
      camera.lookAt(tmp);
      return closeT < 0.4; // false pops the cue; the follow rig resumes
    });
    return () => {
      closing = true;
    };
  }

  // -------------------------------------------------------------------------
  // STANDARD / ATTESTATION typing sheet
  // -------------------------------------------------------------------------

  private buildTyping(
    sheet: HTMLElement,
    spec: ComposeSpec,
    done: (r: ComposeResult) => void
  ): void {
    const isAttest = spec.mode === 'attestation';
    const meter = new CompositionMeter();
    let attestErrors = 0;
    let triplicateStage = 0; // 0 none; 2/3 = current copy number
    let firstCopy = '';
    let ghostCount = 0;
    let lastLen = 0;
    let lastDelAt = 0;
    let delRun = '';

    sheet.innerHTML = '';
    const top = document.createElement('div');
    top.className = 'tractor';
    sheet.appendChild(top);

    const demand = document.createElement('div');
    demand.className = 'compose-demand typewriter';
    const formLine = document.createElement('div');
    formLine.className = 'formid';
    formLine.textContent = spec.formId;
    demand.appendChild(formLine);
    demand.appendChild(document.createTextNode(spec.demand));
    sheet.appendChild(demand);

    const banner = document.createElement('div');
    banner.className = 'triplicate-banner';
    banner.style.display = 'none';
    sheet.appendChild(banner);

    const area = document.createElement('textarea');
    area.className = 'compose-area';
    area.spellcheck = false;
    area.maxLength = 1200;
    area.value = spec.draft || '';
    area.setAttribute('aria-label', spec.formId);
    sheet.appendChild(area);

    const ghosts = document.createElement('div');
    ghosts.className = 'checklist';
    sheet.appendChild(ghosts);

    const checklist = document.createElement('div');
    checklist.className = 'checklist typewriter';
    sheet.appendChild(checklist);

    const foot = document.createElement('div');
    foot.className = 'compose-foot';
    const note = document.createElement('div');
    note.className = 'compose-note';
    note.textContent = isAttest
      ? 'ERRORS RESTART THE LINE. ERRORS ARE COUNTED EITHER WAY.'
      : `FILED AS ${spec.designation || 'UNENROLLED'}`;
    foot.appendChild(note);
    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'slam-btn';
    submitBtn.textContent = spec.submitLabel || 'PULL THE LEVER';
    foot.appendChild(submitBtn);
    sheet.appendChild(foot);

    const bottom = document.createElement('div');
    bottom.className = 'tractor bottom';
    sheet.appendChild(bottom);

    meter.attach(area);
    window.setTimeout(() => area.focus(), 60);

    const renderChecklist = (): void => {
      checklist.innerHTML = '';
      if (isAttest && spec.ritual?.target) {
        const t = normalize(area.value);
        const target = normalize(spec.ritual.target);
        const ok = t === target;
        const partial = target.startsWith(t);
        const row = document.createElement('div');
        row.className = 'row ' + (ok ? 'pass' : 'fail');
        row.innerHTML = `<span class="mark">${ok ? '✓' : partial ? '…' : '✗'}</span>` +
          `<span>attestation ${ok ? 'MATCHES AS PRINTED' : partial ? 'in progress' : 'DEVIATES — the line restarts'}</span>`;
        checklist.appendChild(row);
        return;
      }
      for (const c of liveChecklist(area.value, spec.constraints, spec.designation)) {
        const row = document.createElement('div');
        row.className = 'row ' + (c.pass ? 'pass' : 'fail');
        const mark = document.createElement('span');
        mark.className = 'mark';
        mark.textContent = c.pass ? '✓' : '·';
        const detail = document.createElement('span');
        detail.textContent = c.detail;
        row.append(mark, detail);
        checklist.appendChild(row);
      }
    };
    renderChecklist();

    area.addEventListener('input', () => {
      // attestation: a deviation restarts the line and is counted
      if (isAttest && spec.ritual?.target) {
        const t = normalize(area.value);
        const target = normalize(spec.ritual.target);
        if (t && t !== target && !target.startsWith(t)) {
          attestErrors++;
          area.value = '';
          this.bus.emit('toast', `The line restarts. Recorded errors: ${attestErrors}.`);
        }
      }
      // held-backspace deletions leave faint strike-through ghosts
      const now = performance.now();
      if (area.value.length < lastLen) {
        if (now - lastDelAt < 400) {
          delRun = area.value.length > 0 ? delRun : delRun; // run continues
        } else {
          delRun = '';
        }
        lastDelAt = now;
      } else if (delRun && ghostCount < 2 && lastLen - area.value.length <= 0) {
        delRun = '';
      }
      lastLen = area.value.length;
      renderChecklist();
    });

    // capture what a held backspace erased (the Bureau is curious what it said)
    area.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && spec.closable !== false) {
        finish(true);
        return;
      }
      if (e.key === 'Backspace' && area.selectionStart > 0 && ghostCount < 2) {
        const ch = area.value[area.selectionStart - 1];
        delRun = ch + delRun;
        if (delRun.length === 12) {
          ghostCount++;
          const g = document.createElement('div');
          g.className = 'row';
          g.style.opacity = '0.35';
          g.style.textDecoration = 'line-through';
          g.textContent = delRun;
          ghosts.appendChild(g);
          delRun = '';
        }
      }
    });

    const finish = (abandoned: boolean): void => {
      const telemetry = meter.collect();
      meter.detach();
      done({
        abandoned,
        text: area.value.trim(),
        telemetry,
        imeDowngraded: meter.imeDowngraded,
      });
    };

    submitBtn.addEventListener('click', () => {
      const text = area.value.trim();
      if (!text) {
        this.bus.emit('toast', 'The line for the offering is blank. The Bureau reads blanks. It prefers not to.');
        return;
      }
      if (isAttest && spec.ritual?.target && normalize(text) !== normalize(spec.ritual.target)) {
        attestErrors++;
        this.bus.emit('toast', 'The attestation does not match the printed text. It restarts.');
        area.value = '';
        renderChecklist();
        area.focus();
        return;
      }
      // SUBMIT IN TRIPLICATE: the rare short-offering joke, client-side only
      if (!isAttest && triplicateStage === 0 && text.length < 60 &&
          fnv1a(spec.demandId + ':triplicate') % TRIPLICATE_MOD === 0) {
        triplicateStage = 2;
        firstCopy = text;
        banner.style.display = '';
        banner.textContent = 'OFFERINGS UNDER SIXTY CHARACTERS ARE FILED IN TRIPLICATE. COPY 2 OF 3 — RETYPE AS PRINTED.';
        area.value = '';
        area.placeholder = firstCopy;
        area.focus();
        return;
      }
      if (triplicateStage > 0) {
        if (normalize(text) !== normalize(firstCopy)) {
          this.bus.emit('toast', 'The copies must agree. The Bureau compares them. That is the point of triplicate.');
          area.value = '';
          area.focus();
          return;
        }
        if (triplicateStage === 2) {
          triplicateStage = 3;
          banner.textContent = 'COPY 3 OF 3 — RETYPE AS PRINTED. THE BUREAU THANKS NO ONE.';
          area.value = '';
          area.focus();
          return;
        }
        area.value = firstCopy; // all three agree; the original is filed
      }
      finish(false);
    });
  }

  // -------------------------------------------------------------------------
  // PATIENCE — stillness with a generous 0.25 deadzone; zero inference
  // -------------------------------------------------------------------------

  private buildPatience(
    sheet: HTMLElement,
    spec: ComposeSpec,
    done: (r: ComposeResult) => void
  ): void {
    const holdS = spec.ritual?.holdSeconds || 15;
    sheet.innerHTML = '';
    sheet.appendChild(el('div', 'tractor'));
    const demand = document.createElement('div');
    demand.className = 'compose-demand typewriter';
    const formLine = el('div', 'formid');
    formLine.textContent = spec.formId;
    demand.appendChild(formLine);
    demand.appendChild(document.createTextNode(spec.demand));
    sheet.appendChild(demand);

    const track = el('div', 'hold-track');
    const fill = el('div', 'hold-fill');
    track.appendChild(fill);
    sheet.appendChild(track);

    const status = el('div', 'compose-note');
    status.style.margin = '0 20px 10px';
    status.textContent = `REMAIN STILL FOR ${holdS} SECONDS. THE BUREAU WILL COUNT THEM.`;
    sheet.appendChild(status);

    const foot = el('div', 'compose-foot');
    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'slam-btn secondary';
    leave.textContent = 'STEP AWAY';
    foot.appendChild(leave);
    sheet.appendChild(foot);
    sheet.appendChild(el('div', 'tractor bottom'));

    let held = 0;
    let total = 0;
    let raf = 0;
    let last = performance.now();
    const pressed = new Set<string>();
    const MOVE_KEYS = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    const onDown = (e: KeyboardEvent): void => {
      if (MOVE_KEYS.includes(e.code)) pressed.add(e.code);
      if (e.key === 'Escape' && spec.closable !== false) cleanup(true, false);
    };
    const onUp = (e: KeyboardEvent): void => {
      pressed.delete(e.code);
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);

    const cleanup = (abandoned: boolean, completed: boolean): void => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      done({
        abandoned,
        text: completed ? 'STILLNESS OBSERVED AS PRINTED.' : '',
        telemetry: { seconds: Math.ceil(completed ? Math.max(held, holdS) : total) },
        completed,
        imeDowngraded: false,
      });
    };

    const step = (): void => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      total += dt;
      // digital keys are full deflection: any movement key exceeds the 0.25
      // deadzone; drift-prone hardware never reads as judgment.
      const fidget = pressed.size > 0;
      if (fidget && held > 0.2) {
        held = 0;
        sheet.classList.add('blurred-ink');
        status.textContent = 'MOTION DETECTED. THE INK BLURS. THE LINE RESTARTS.';
        window.setTimeout(() => sheet.classList.remove('blurred-ink'), 450);
      } else if (!fidget) {
        held += dt;
      }
      fill.style.width = `${Math.min(100, (held / holdS) * 100).toFixed(1)}%`;
      if (held >= holdS) {
        status.textContent = 'STILLNESS OBSERVED IN FULL. THIS IS NOTED.';
        window.setTimeout(() => cleanup(false, true), 500);
        return;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    leave.addEventListener('click', () => cleanup(true, false));
  }

  // -------------------------------------------------------------------------
  // SORTING — the pile is patient; the pile is always patient
  // -------------------------------------------------------------------------

  private buildSorting(
    sheet: HTMLElement,
    spec: ComposeSpec,
    done: (r: ComposeResult) => void
  ): void {
    sheet.innerHTML = '';
    sheet.appendChild(el('div', 'tractor'));
    const demand = document.createElement('div');
    demand.className = 'compose-demand typewriter';
    const formLine = el('div', 'formid');
    formLine.textContent = spec.formId;
    demand.appendChild(formLine);
    demand.appendChild(document.createTextNode(spec.demand));
    sheet.appendChild(demand);

    const note = el('div', 'compose-note');
    note.style.margin = '0 20px 4px';
    note.textContent = 'FILE THE FORMS IN ASCENDING ORDER. MISTAKES RETURN THE PILE.';
    sheet.appendChild(note);

    const grid = el('div', 'sorting-grid');
    sheet.appendChild(grid);

    // deterministic pile from the demand id — every retry is the same pile
    const nums: number[] = [];
    for (let i = 0; i < 6; i++) nums.push(100 + (fnv1a(spec.demandId + ':pile:' + i) % 890));
    const sorted = [...new Set(nums)].sort((a, b) => a - b);
    const shuffled = [...new Set(nums)];
    // seeded shuffle
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = fnv1a(spec.demandId + ':shuf:' + i) % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    let next = 0;
    const t0 = performance.now();
    const foot = el('div', 'compose-foot');
    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'slam-btn secondary';
    leave.textContent = 'STEP AWAY';
    foot.appendChild(leave);
    sheet.appendChild(foot);
    sheet.appendChild(el('div', 'tractor bottom'));

    const finish = (abandoned: boolean, completed: boolean): void => {
      done({
        abandoned,
        text: completed ? 'THE PILE IS FILED IN ORDER.' : '',
        telemetry: { seconds: Math.round((performance.now() - t0) / 1000) },
        completed,
        imeDowngraded: false,
      });
    };

    for (const n of shuffled) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = `7A-${n}`;
      b.addEventListener('click', () => {
        if (sorted[next] === n) {
          b.classList.add('sorted');
          b.disabled = true;
          next++;
          if (next >= sorted.length) {
            note.textContent = 'THE PILE IS FILED. THE PILE IS AT PEACE.';
            window.setTimeout(() => finish(false, true), 500);
          }
        } else {
          next = 0;
          note.textContent = 'OUT OF ORDER. THE PILE RETURNS. THE PILE IS PATIENT.';
          for (const c of Array.from(grid.children) as HTMLButtonElement[]) {
            c.classList.remove('sorted');
            c.disabled = false;
          }
        }
      });
      grid.appendChild(b);
    }
    leave.addEventListener('click', () => finish(true, false));
  }
}

function normalize(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function el(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}
